/* modern_metrovibe app entry — vanilla ES, no deps */
const MV = {
  on: (name, fn) => {
    document.addEventListener(name, fn);
    return () => document.removeEventListener(name, fn);
  },
  off: (name, fn) => document.removeEventListener(name, fn),
  emit: (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail })),
  money: (paise) => "₹" + (paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 }),
  debounce(fn, ms = 200) {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  },
  reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  _scrollLocks: 0,
  lockScroll: () => { MV._scrollLocks++; if (MV._scrollLocks === 1) document.documentElement.style.overflow = "hidden"; },
  unlockScroll: () => { MV._scrollLocks = Math.max(0, MV._scrollLocks - 1); if (MV._scrollLocks === 0) document.documentElement.style.overflow = ""; },
  root: () => window.Shopify?.routes?.root || "/",
  escapeHtml: (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c])),
};
window.MV = MV;

const esc = MV.escapeHtml;

class MVRotator extends HTMLElement {
  connectedCallback() {
    this.items = [...this.children];
    this.items.forEach((el, i) => el.toggleAttribute("hidden", i !== 0));
    this.index = 0;
    this.setAttribute("data-ready", "");

    if (!this.blockSelectBound) {
      this.onBlockSelect = (e) => {
        const blockId = e.detail?.blockId;
        const idx = blockId ? this.items.findIndex((el) => el.dataset.blockId === blockId) : -1;
        if (idx === -1) return;
        clearInterval(this.timer);
        this.items[this.index]?.toggleAttribute("hidden", true);
        this.index = idx;
        this.items[idx].hidden = false;
      };
      this.offBlockSelect = MV.on("shopify:block:select", this.onBlockSelect);
      this.blockSelectBound = true;
    }

    if (this.items.length < 2 || MV.reducedMotion) return;
    const interval = Number(this.getAttribute("interval")) || 4000;
    this.timer = setInterval(() => this.next(), interval);
    if (!this.hoverBound) {
      this.addEventListener("mouseenter", () => clearInterval(this.timer));
      this.addEventListener("mouseleave", () => (this.timer = setInterval(() => this.next(), interval)));
      this.hoverBound = true;
    }
  }
  next() {
    this.items[this.index].hidden = true;
    this.index = (this.index + 1) % this.items.length;
    this.items[this.index].hidden = false;
  }
  disconnectedCallback() {
    clearInterval(this.timer);
    this.offBlockSelect?.();
    this.blockSelectBound = false;
  }
}
customElements.define("mv-rotator", MVRotator);

class MVCountdown extends HTMLElement {
  connectedCallback() {
    this.deadline = new Date(this.getAttribute("deadline")).getTime();
    if (Number.isNaN(this.deadline)) { this.expire(); return; }
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }
  tick() {
    const ms = this.deadline - Date.now();
    if (ms <= 0) { this.render(0); this.expire(); return; }
    this.render(ms);
  }
  render(ms) {
    const d = Math.floor(ms / 86400000);
    const h = String(Math.floor(ms / 3600000) % 24).padStart(2, "0");
    const m = String(Math.floor(ms / 60000) % 60).padStart(2, "0");
    const s = String(Math.floor(ms / 1000) % 60).padStart(2, "0");
    this.textContent = (d > 0 ? d + "d " : "") + h + ":" + m + ":" + s;
  }
  expire() {
    // final render (via tick) already wrote the zeroed-out time before we get here,
    // so there's no ~1s-stale text lingering after the interval is cleared / element hidden
    clearInterval(this.timer);
    const sel = this.getAttribute("hide-closest");
    if (sel) this.closest(sel)?.style.setProperty("display", "none");
    if (this.getAttribute("hide-target") === "section") {
      this.closest(".shopify-section")?.style.setProperty("display", "none");
    }
    MV.emit("countdown:expired", { el: this });
  }
  disconnectedCallback() { clearInterval(this.timer); }
}
customElements.define("mv-countdown", MVCountdown);

class MVHeader extends HTMLElement {
  connectedCallback() {
    this.count = this.querySelector("#CartCount");
    this.onCartUpdated = (e) => {
      const n = e.detail?.cart?.item_count ?? 0;
      this.count.textContent = n;
      this.count.classList.toggle("is-empty", n === 0);
    };
    this.offCart = MV.on("cart:updated", this.onCartUpdated);

    this.searchToggle = this.querySelector("#SearchToggle");
    this.onSearchToggleClick = (e) => { e.preventDefault(); MV.emit("search:open"); };
    this.searchToggle?.addEventListener("click", this.onSearchToggleClick);

    if (this.dataset.sticky !== "true") return;
    this.classList.add("is-sticky");
    let last = window.scrollY;
    this.onScroll = () => {
      const y = window.scrollY;
      const goingDown = y > last && y > 120;
      this.classList.toggle("is-hidden", goingDown);
      last = y;
    };
    window.addEventListener("scroll", this.onScroll, { passive: true });
  }
  disconnectedCallback() {
    this.offCart?.();
    this.searchToggle?.removeEventListener("click", this.onSearchToggleClick);
    if (this.dataset.sticky === "true") {
      window.removeEventListener("scroll", this.onScroll);
    }
  }
}
customElements.define("mv-header", MVHeader);

class MVDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    const openOn = this.getAttribute("open-on");
    if (openOn) this.offOpenOn = MV.on(openOn, () => this.show());
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) this.hide(); // backdrop click
      if (e.target.closest("[data-drawer-close]")) this.hide();
      // mobile menu: a data-mnav-toggle (chevron, or the whole header when the parent has
      // no real destination) expands the submenu. Drive state from the submenu itself so
      // multiple toggles in one group stay in sync (Bug 8).
      const toggle = e.target.closest("[data-mnav-toggle]");
      if (toggle) {
        const group = toggle.closest("[data-mnav-group]");
        const sub = group?.querySelector(".mv-mnav__sublist");
        if (sub) {
          const willOpen = sub.hidden;
          sub.hidden = !willOpen;
          group.querySelectorAll("[data-mnav-toggle]").forEach((t) => t.setAttribute("aria-expanded", String(willOpen)));
        }
      }
    });
    this.dialog.addEventListener("cancel", (e) => { e.preventDefault(); this.hide(); });
    // swipe-to-close
    let startX = null;
    this.dialog.addEventListener("touchstart", (e) => (startX = e.touches[0].clientX), { passive: true });
    this.dialog.addEventListener("touchend", (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      const side = this.getAttribute("side") || "left";
      if ((side === "left" && dx < -60) || (side === "right" && dx > 60)) this.hide();
      startX = null;
    }, { passive: true });
  }
  show() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    MV.lockScroll();
  }
  hide() {
    if (this.dialog.classList.contains("is-closing") || !this.dialog.open) return;
    const panel = this.dialog.querySelector(".mv-drawer__panel");
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      panel?.removeEventListener("transitionend", onTransitionEnd);
      this.dialog.classList.remove("is-closing");
      this.dialog.close();
      MV.unlockScroll();
    };
    // only the panel's own translate transition should close the drawer (not a bubbled child transitionend)
    const onTransitionEnd = (e) => { if (e.target === panel && e.propertyName === "translate") finish(); };
    if (MV.reducedMotion || !panel) { finish(); return; } // no panel (full-screen search) — close immediately
    this.dialog.classList.add("is-closing");
    panel.addEventListener("transitionend", onTransitionEnd);
    setTimeout(finish, 300); // safety net if transitionend never fires
  }
  disconnectedCallback() {
    this.offOpenOn?.();
  }
}
customElements.define("mv-drawer", MVDrawer);

class MVSearchOverlay extends MVDrawer {
  connectedCallback() {
    super.connectedCallback();
    this.input = this.querySelector(".mv-search__input");
    this.results = this.querySelector("#SearchResults");
    this.input.addEventListener("input", MV.debounce(() => this.query(), 250));
  }
  show() { super.show(); this.input.focus(); }
  async query() {
    const q = this.input.value.trim();
    this.controller?.abort();
    if (q.length < 2) { this.results.innerHTML = ""; return; }
    this.controller = new AbortController();
    this.results.innerHTML = '<div class="mv-skeleton" style="height:56px"></div>'.repeat(3);
    try {
      const url = `${MV.root()}search/suggest.json?q=${encodeURIComponent(q)}` +
        `&resources[type]=product,collection,query&resources[limit]=6`;
      const res = await fetch(url, { signal: this.controller.signal });
      const { resources } = await res.json();
      this.render(q, resources.results);
    } catch (err) {
      if (err?.name === "AbortError") return; // superseded by a newer query — leave DOM alone
      this.results.innerHTML = '<p class="mv-search__label">Search is unavailable — press Enter to search.</p>';
    }
  }
  render(q, { products = [], collections = [], queries = [] }) {
    let html = "";
    if (queries.length) {
      // styled_text is Shopify's own predictive-search markup — intentionally unescaped
      html += queries.map((s) => `<a class="mv-search__item" href="${esc(s.url)}">${s.styled_text}</a>`).join("");
    }
    if (collections.length) {
      html += `<p class="mv-search__label">Collections</p>` +
        collections.map((c) => `<a class="mv-search__item" href="${esc(c.url)}">${esc(c.title)}</a>`).join("");
    }
    if (products.length) {
      html += `<p class="mv-search__label">Products</p>` + products.map((p) => {
        const price = parseFloat(p.price_min ?? p.price);
        return `
        <a class="mv-search__item" href="${esc(p.url)}">
          ${p.featured_image ? `<img src="${esc(p.featured_image.url + (p.featured_image.url.includes("?") ? "&" : "?") + "width=104")}" alt="" loading="lazy">` : ""}
          <span>
            <span class="mv-search__item-title">${esc(p.title)}</span><br>
            ${Number.isFinite(price) ? `<span class="mv-search__item-price">${MV.money(price * 100)}</span>` : ""}
          </span>
        </a>`;
      }).join("");
    }
    const searchUrl = MV.root() + "search?q=" + encodeURIComponent(q);
    html += html
      ? `<a class="mv-search__all" href="${searchUrl}">View all results →</a>`
      : `<p class="mv-search__label">No matches for “${esc(q)}” — press Enter to search everything.</p>`;
    this.results.innerHTML = html;
  }
}
customElements.define("mv-search-overlay", MVSearchOverlay);

class MVCarousel extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.track = this.querySelector(".mv-carousel__track");
    this.slides = [...this.track.children];

    this.offBlockSelect = MV.on("shopify:block:select", (e) => {
      const idx = this.slides.findIndex((s) => s.dataset.blockId === e.detail?.blockId);
      if (idx >= 0) {
        this.stopAuto();
        this.go(idx);
      }
    });

    if (this.slides.length < 2) return;
    this.buildChrome();
    this.track.addEventListener("scroll", MV.debounce(() => this.sync(), 80), { passive: true });
    this.autoDelay = Number(this.getAttribute("autoplay")) || 0;
    this.startAuto();
    this.addEventListener("pointerenter", () => this.stopAuto());
    this.addEventListener("pointerleave", () => this.startAuto());
    this.addEventListener("focusin", () => this.stopAuto());
    this.addEventListener("focusout", () => this.startAuto());
  }
  startAuto() {
    this.stopAuto();
    if (this.autoDelay >= 1000 && !MV.reducedMotion) {
      this.timer = setInterval(() => this.next(), this.autoDelay);
    }
  }
  stopAuto() {
    clearInterval(this.timer);
  }
  buildChrome() {
    const dots = document.createElement("div");
    dots.className = "mv-carousel__dots";
    this.dots = this.slides.map((_, i) => {
      const b = document.createElement("button");
      b.className = "mv-carousel__dot";
      b.type = "button";
      b.setAttribute("aria-label", `Slide ${i + 1}`);
      b.addEventListener("click", () => this.go(i));
      dots.appendChild(b);
      return b;
    });
    this.appendChild(dots);
    ["prev", "next"].forEach((dir) => {
      const b = document.createElement("button");
      b.className = `mv-carousel__arrow mv-carousel__arrow--${dir}`;
      b.type = "button";
      b.setAttribute("aria-label", dir === "prev" ? "Previous slide" : "Next slide");
      b.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>';
      b.addEventListener("click", () => (dir === "prev" ? this.go(this.index() - 1) : this.next()));
      this.appendChild(b);
    });
    this.sync();
  }
  index() {
    const w = this.track.clientWidth;
    return w ? Math.round(this.track.scrollLeft / w) : 0;
  }
  go(i) {
    const n = this.slides.length;
    const target = ((i % n) + n) % n;
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: MV.reducedMotion ? "auto" : "smooth" });
  }
  next() { this.go(this.index() + 1); }
  sync() {
    const cur = this.index();
    this.dots?.forEach((d, i) => d.setAttribute("aria-current", i === cur ? "true" : "false"));
  }
  disconnectedCallback() {
    this.stopAuto();
    this.offBlockSelect?.();
    this.bound = false;
  }
}
customElements.define("mv-carousel", MVCarousel);

MV.toast = (msg) => {
  let el = document.getElementById("MVToast");
  if (!el) {
    el = document.createElement("div");
    el.id = "MVToast";
    el.className = "mv-toast";
    el.setAttribute("role", "status");
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.classList.add("is-visible");
  clearTimeout(MV._toastTimer);
  MV._toastTimer = setTimeout(() => el.classList.remove("is-visible"), 2600);
};

const WL_KEY = "mv:wishlist";
MV.wishlist = {
  all() {
    try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; } catch { return []; }
  },
  has(handle) { return this.all().includes(handle); },
  toggle(handle) {
    const list = this.all();
    const i = list.indexOf(handle);
    if (i >= 0) list.splice(i, 1); else list.push(handle);
    try { localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch {}
    MV.emit("wishlist:updated", { handles: list });
    return i < 0;
  },
  // drop handles whose product fetch 404'd (deleted/unpublished) so they stop cluttering the wishlist
  prune(deadHandles) {
    if (!deadHandles?.length) return;
    const list = this.all().filter((h) => !deadHandles.includes(h));
    try { localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch {}
    MV.emit("wishlist:updated", { handles: list });
  },
};

class MVWishlistButton extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.btn = this.querySelector("button");
    if (!this.btn) return;
    this.sync();
    this.btn.addEventListener("click", () => {
      const added = MV.wishlist.toggle(this.dataset.handle);
      MV.toast(added ? "Saved to wishlist" : "Removed from wishlist");
    });
    this.offWl = MV.on("wishlist:updated", () => this.sync());
  }
  sync() {
    const on = MV.wishlist.has(this.dataset.handle);
    this.btn.setAttribute("aria-pressed", on ? "true" : "false");
    this.btn.classList.toggle("is-active", on);
    this.btn.setAttribute("aria-label", on ? "Remove from wishlist" : "Add to wishlist");
  }
  disconnectedCallback() { this.offWl?.(); }
}
customElements.define("mv-wishlist-button", MVWishlistButton);

class MVQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.dialog = this.querySelector("dialog");
    this.body = this.querySelector("#QuickAddBody");
    this.offOpen = MV.on("quickadd:open", (e) => this.open(e.detail.handle, e.detail.source));
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog || e.target.closest("[data-sheet-close]")) this.close();
    });
    this.dialog.addEventListener("cancel", (e) => { e.preventDefault(); this.close(); });
  }
  // guard lock/unlock to the actual open/close transition so the ref-counter stays balanced:
  // openDialog may be called on the error-retry path when already open, and close() may run
  // on the single-variant add path where the sheet was never opened
  close() { if (this.dialog.open) { this.dialog.close(); MV.unlockScroll(); } }
  openDialog() { if (!this.dialog.open) { this.dialog.showModal(); MV.lockScroll(); } }
  async open(handle, source) {
    this.lastHandle = handle;
    this.source = source || null; // the card's Quick add button, flashed on success (Bug 7)
    try {
      const root = MV.root();
      const res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) throw new Error(res.status);
      this.product = await res.json();
      if (this.product.variants.length === 1) {
        // single-variant: add directly
        if (this.product.variants[0].available) { await this.add(this.product.variants[0].id); return; }
        // sold-out: fall through to disabled sheet
      }
      this.openDialog();
      this.render();
    } catch {
      this.openDialog();
      this.body.innerHTML = `<p class="mv-sheet__error">Couldn't load sizes.</p>
        <button type="button" class="mv-btn mv-btn--ghost" data-qa-retry>Tap to retry</button>`;
      this.body.querySelector("[data-qa-retry]").addEventListener("click", () => this.open(this.lastHandle));
    }
  }
  render() {
    const p = this.product;
    this.selected = null;
    const sep = p.featured_image && p.featured_image.includes("?") ? "&" : "?";
    this.body.innerHTML = `
      <div class="mv-sheet__product">
        ${p.featured_image ? `<img src="${esc(p.featured_image)}${sep}width=112" alt="">` : ""}
        <span><strong>${esc(p.title)}</strong><br><span class="mv-sheet__price" id="QaPrice">${MV.money(p.price)}</span></span>
      </div>
      <div class="mv-sheet__grid" role="group" aria-label="Size">
        ${p.variants.map((v) => `
          <button type="button" class="mv-sheet__opt" data-vid="${v.id}" data-price="${v.price}"
            aria-pressed="false" ${v.available ? "" : "disabled"}>${esc(v.title)}</button>`).join("")}
      </div>
      <button type="button" class="mv-btn mv-btn--primary mv-sheet__atc" id="QaAtc" disabled>Add to cart</button>
      <a class="mv-sheet__viewcart" href="${MV.root()}cart" hidden>View cart →</a>`;
    const atc = this.body.querySelector("#QaAtc");
    this.body.querySelectorAll(".mv-sheet__opt").forEach((b) => {
      b.addEventListener("click", () => {
        this.body.querySelectorAll(".mv-sheet__opt").forEach((o) => o.setAttribute("aria-pressed", "false"));
        b.setAttribute("aria-pressed", "true");
        this.selected = b.dataset.vid;
        this.body.querySelector("#QaPrice").textContent = MV.money(Number(b.dataset.price));
        atc.disabled = false;
        // reset out of any prior "Added" state so a newly-picked size can be added
        atc.classList.remove("is-added");
        atc.textContent = "Add to cart";
      });
    });
    atc.addEventListener("click", () => this.add(this.selected));
  }
  async add(variantId) {
    try {
      const root = MV.root();
      const res = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      if (!res.ok) throw new Error(res.status);
      const cart = await (await fetch(`${root}cart.js`)).json();
      MV.emit("cart:updated", { cart });
      if (this.dialog.open) {
        // Added from the size sheet: confirm the variant inline and keep the sheet open
        // (no auto-opening drawer) so the shopper sees which size went in.
        this.showAddedInSheet(variantId);
        MV.toast("Added to cart");
      } else {
        // Single-variant direct add: flash the card button and open the cart drawer.
        this.close();
        this.flashSource();
        MV.emit("cart:added", { cart });
        MV.toast("Added to cart");
      }
    } catch {
      const atc = this.body.querySelector("#QaAtc");
      if (atc) { atc.textContent = "Couldn't add — tap to retry"; atc.disabled = false; }
      else {
        this.body.innerHTML = `<p class="mv-sheet__error">Couldn't add — please try again.</p><button type="button" class="mv-btn mv-btn--primary" data-qa-retry>Retry</button>`;
        this.openDialog();
        this.body.querySelector("[data-qa-retry]").addEventListener("click", () => this.add(variantId));
      }
    }
  }
  // Mark the just-added variant in the still-open size sheet and reveal a "View cart" link,
  // so the shopper sees which size they added and can add another.
  showAddedInSheet(variantId) {
    const pill = this.body.querySelector(`.mv-sheet__opt[data-vid="${variantId}"]`);
    if (pill) pill.classList.add("is-added");
    const atc = this.body.querySelector("#QaAtc");
    if (atc) { atc.classList.add("is-added"); atc.textContent = "Added to cart"; atc.disabled = true; }
    const viewCart = this.body.querySelector(".mv-sheet__viewcart");
    if (viewCart) viewCart.hidden = false;
  }
  // Flash the originating card's Quick add button to a confirmed "Added" state (Bug 7)
  flashSource() {
    const btn = this.source;
    if (!btn || !btn.isConnected) return;
    if (btn._mvRevert) clearTimeout(btn._mvRevert);
    else btn._mvOriginal = btn.innerHTML;
    btn.classList.add("is-added");
    btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"/></svg> Added';
    btn._mvRevert = setTimeout(() => {
      btn.innerHTML = btn._mvOriginal;
      btn.classList.remove("is-added");
      btn._mvRevert = null;
    }, 1800);
  }
  disconnectedCallback() { this.offOpen?.(); }
}
customElements.define("mv-quick-add", MVQuickAdd);
